import { Close, CreditCard, Refresh, Save, Search, InboxOutlined } from "@mui/icons-material";
import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  CircularProgress,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Snackbar,
  Stack,
  TextField,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { ApplicationCard } from "../components/ApplicationCard";
import { DOC_TYPES } from "../config/config";
import { useAllApplications } from "./hooks/useAllApplications";

const AllApplicationsPage: React.FC = () => {
  const {
    userData,
    loadApplications,
    loading,
    openCreate,
    user,
    items,
    fmtMoney,
    StatusChip,
    openEdit,
    isEdit,
    canEditIdentity,
    form,
    handleChange,
    errors,
    closeDialog,
    saveForm,
    open,
    loadingDefault,
    enableConsent,
    declineApplication,
    snackbar,
    setSnackbar,
    updateInstallment,
    finalizeApplication,
    uploadInstallment,
    uploadConsent,
  } = useAllApplications();

  const theme = useTheme();
  const isMobile = true;

  const [filterStatus, setFilterStatus] = useState<
    "ALL" | "PENDING" | "APPROVED" | "FINISHED"
  >("APPROVED");
  const [visibleCount, setVisibleCount] = useState(5);
  const [openId, setOpenId] = useState<string | null>(null);
  const [confirmDecline, setConfirmDecline] = useState<{
    open: boolean;
    rowId?: string;
  }>({ open: false });
  const [searchText, setSearchText] = useState("");

  const sortedItems = items;

  const filteredItems = sortedItems.filter((row) => {
    const matchStatus = filterStatus === "ALL" || row.status === filterStatus;
    const matchSearch = !searchText || (row.applicantName || "").toLowerCase().includes(searchText.toLowerCase());
    return matchStatus && matchSearch;
  });

  const counts = {
    ALL: items.length,
    PENDING: items.filter((r) => r.status === "PENDING").length,
    APPROVED: items.filter((r) => r.status === "APPROVED").length,
    FINISHED: items.filter((r) => r.status === "FINISHED").length,
  };

  const visibleItems = filteredItems.slice(0, visibleCount);

  const toggleSx = {
    "&.Mui-selected": {
      bgcolor: "primary.main",
      color: "primary.contrastText",
      "&:hover": { bgcolor: "primary.dark" },
    },
  };

  const handleDeclineClick = (rowId: string) => {
    setConfirmDecline({ open: true, rowId });
  };

  const confirmDeclineApplication = async () => {
    if (confirmDecline.rowId) {
      await declineApplication(confirmDecline.rowId);
    }
    setConfirmDecline({ open: false });
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Card
        elevation={0}
        sx={{ borderRadius: 3, border: "1px solid", borderColor: "divider" }}
      >
        <CardHeader
          title="Solicitudes de crédito"
          subheader={`Gestiona todas las solicitudes`}
          action={
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              sx={{ width: { xs: "100%", sm: "auto" } }}
            >
              <Button
                fullWidth={isMobile}
                variant="outlined"
                startIcon={<Refresh />}
                onClick={loadApplications}
                disabled={loading}
                sx={{ borderRadius: "50px", textTransform: "none", fontWeight: 600, borderColor: "#e0e0e0", color: "#667788", fontSize: 13 }}
              >
                Actualizar
              </Button>
              {/* <Button
                fullWidth={isMobile}
                variant="contained"
                startIcon={<Add />}
                disableElevation
                onClick={openCreate}
              >
                Nueva solicitud
              </Button> */}
            </Stack>
          }
          sx={{ pb: 2 }}
        />
      </Card>

      {/* Buscador */}
      <TextField
        fullWidth
        size="small"
        placeholder="Buscar por nombre del cliente..."
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search sx={{ color: "#8899AA", fontSize: 20 }} />
            </InputAdornment>
          ),
          endAdornment: searchText ? (
            <InputAdornment position="end">
              <IconButton size="small" onClick={() => setSearchText("")}>
                <Close sx={{ fontSize: 16, color: "#8899AA" }} />
              </IconButton>
            </InputAdornment>
          ) : null,
          sx: { borderRadius: "50px", bgcolor: "#f7f9f7" },
        }}
        sx={{ mb: 1 }}
      />
      {/* Filtros modernos - chips */}
      <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
        {[
          { value: "APPROVED", label: "Aprobadas", count: counts.APPROVED, color: "#00C853" },
          { value: "PENDING", label: "Pendientes", count: counts.PENDING, color: "#f59e0b" },
          { value: "FINISHED", label: "Finalizadas", count: counts.FINISHED, color: "#8899AA" },
          { value: "ALL", label: "Todas", count: counts.ALL, color: "#0D1420" },
        ].map((filter) => (
          <Box
            key={filter.value}
            onClick={() => setFilterStatus(filter.value as any)}
            sx={{
              display: "flex", alignItems: "center", gap: 1,
              px: 2, py: 0.8,
              borderRadius: "50px",
              cursor: "pointer",
              border: `2px solid ${filterStatus === filter.value ? filter.color : "#e0e0e0"}`,
              bgcolor: filterStatus === filter.value ? `${filter.color}15` : "transparent",
              transition: "all 0.2s ease",
              "&:hover": { borderColor: filter.color, bgcolor: `${filter.color}10` },
            }}
          >
            <Box sx={{ fontSize: 13, fontWeight: filterStatus === filter.value ? 700 : 500, color: filterStatus === filter.value ? filter.color : "#667788" }}>
              {filter.label}
            </Box>
            <Box sx={{ bgcolor: filterStatus === filter.value ? filter.color : "#e0e0e0", color: filterStatus === filter.value ? "white" : "#667788", borderRadius: "50px", px: 1, py: 0.1, fontSize: 11, fontWeight: 700, minWidth: 20, textAlign: "center" }}>
              {filter.count}
            </Box>
          </Box>
        ))}
      </Box>

      <Grid container spacing={2} sx={{ maxWidth: 1600 }}>
        {visibleItems.length === 0 ? (
          <Grid item xs={12}>
            {loading ? (
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", py: 8, gap: 2 }}>
                <Box sx={{ position: "relative", width: 70, height: 70 }}>
                  <CircularProgress size={70} thickness={2} sx={{ color: "#00C85322", position: "absolute" }} variant="determinate" value={100} />
                  <CircularProgress size={70} thickness={2} sx={{ color: "#00C853", position: "absolute" }} />
                  <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <CreditCard sx={{ fontSize: 32, color: "#00C853" }} />
                  </Box>
                </Box>
                <Typography sx={{ fontWeight: 700, fontSize: 15, color: "#0D1420" }}>Cargando solicitudes...</Typography>
                <Typography sx={{ fontSize: 13, color: "#8899AA" }}>Trayendo la información de tus créditos</Typography>
              </Box>
            ) : (
              <Box sx={{ textAlign: "center", py: 8 }}>
                <InboxOutlined sx={{ fontSize: 40, color: "#8899AA", mb: 1 }} />
                <Typography sx={{ fontWeight: 700, fontSize: 15, color: "#0D1420" }}>No hay solicitudes</Typography>
                <Typography sx={{ fontSize: 13, color: "#8899AA" }}>No hay créditos en esta categoría</Typography>
              </Box>
            )}
          </Grid>
        ) : (
          visibleItems.map((row) => (
            <Grid item key={row.id} xs={12} sm={6} md={4}>
              <ApplicationCard
                row={row}
                fmtMoney={fmtMoney}
                StatusChip={StatusChip}
                open={openId === row.id}
                onToggle={() => setOpenId(openId === row.id ? null : row.id!)}
                enableConsent={enableConsent}
                declineApplication={async () => handleDeclineClick(row.id!)}
                loadingDefault={loadingDefault}
                user={user!}
                loadApplications={loadApplications}
                isAdmin={true}
                uploadInstallment={uploadInstallment}
                uploadConsent={uploadConsent}
                updateInstallment={updateInstallment}
                finalizeApplication={finalizeApplication}
              />
            </Grid>
          ))
        )}
      </Grid>

      {/* Ver más */}
      {visibleCount < filteredItems.length && (
        <Box textAlign="center" mt={2}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setVisibleCount((c) => c + 5)}
            sx={{ borderRadius: "50px", textTransform: "none", fontWeight: 600, borderColor: "#00C853", color: "#00C853", px: 3, "&:hover": { bgcolor: "#00C85311", borderColor: "#00C853" } }}
          >
            Ver más
          </Button>
        </Box>
      )}
      <Dialog open={open} onClose={closeDialog} fullWidth maxWidth="md">
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {isEdit ? "Editar solicitud" : "Nueva solicitud"}
          <Box flex={1} />
          <IconButton onClick={closeDialog} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent dividers>
          <Grid container spacing={2}>
            {canEditIdentity.name ? (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  label="Nombre del solicitante"
                  value={form.applicantName || ""}
                  onChange={handleChange("applicantName")}
                  error={!!errors.applicantName}
                  helperText={errors.applicantName || ""}
                  inputProps={{ maxLength: 80 }}
                />
              </Grid>
            ) : (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Nombre del solicitante"
                  value={userData.data?.fullName}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
            )}

            {canEditIdentity.doc ? (
              <>
                <Grid item xs={12} md={3}>
                  <TextField
                    select
                    fullWidth
                    required
                    label="Tipo de documento"
                    value={form.documentType || "CC"}
                    onChange={handleChange("documentType")}
                    error={!!errors.documentType}
                    helperText={errors.documentType || ""}
                  >
                    {DOC_TYPES.map((t) => (
                      <MenuItem key={t} value={t}>
                        {t}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    required
                    label="Documento"
                    value={form.document || ""}
                    onChange={handleChange("document")}
                    error={!!errors.document}
                    helperText={errors.document || ""}
                    inputProps={{ maxLength: 20 }}
                  />
                </Grid>
              </>
            ) : (
              <>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Tipo de documento"
                    value={userData.data?.documentType}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Documento"
                    value={userData.data?.document}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                required
                label="Monto solicitado (COP)"
                type="number"
                value={form.amount ?? ""}
                onChange={handleChange("amount")}
                error={!!errors.amount}
                helperText={errors.amount || ""}
                inputProps={{ min: 0, step: 100000 }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                required
                label="Plazo (meses)"
                type="number"
                value={form.termMonths ?? ""}
                onChange={handleChange("termMonths")}
                error={!!errors.termMonths}
                helperText={errors.termMonths || ""}
                inputProps={{ min: 6, max: 84 }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                required
                label="Propósito"
                value={form.purpose || ""}
                onChange={handleChange("purpose")}
                error={!!errors.purpose}
                inputProps={{ maxLength: 120 }}
                placeholder="Ejemplos: Compra de vehículo, remodelación, estudios, pago de deudas..."
              />
            </Grid>

            {canEditIdentity.email ? (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Correo alterno (opcional)"
                  type="email"
                  value={form.emailOptional || ""}
                  onChange={handleChange("emailOptional")}
                  error={!!errors.emailOptional}
                  helperText={errors.emailOptional || ""}
                  inputProps={{ maxLength: 120 }}
                />
              </Grid>
            ) : (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Correo"
                  value={user?.email || ""}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
            )}

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Teléfono (del perfil)"
                value={user?.phoneNumber || "—"}
                InputProps={{ readOnly: true }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={closeDialog}>Cancelar</Button>
          <Button
            variant="contained"
            startIcon={<Save />}
            disableElevation
            onClick={saveForm}
          >
            {isEdit ? "Guardar cambios" : "Crear solicitud"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmDecline.open}
        onClose={() => setConfirmDecline({ open: false })}
      >
        <DialogTitle>Confirmar acción</DialogTitle>
        <DialogContent>
          ¿Está seguro que desea declinar esta solicitud?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDecline({ open: false })}>
            Cancelar
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={confirmDeclineApplication}
            loading={loadingDefault}
          >
            Sí, declinar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{
            width: "100%",
            borderRadius: 3,
            fontWeight: 600,
            bgcolor: snackbar.severity === "success" ? "#00C853" : snackbar.severity === "error" ? "#ef4444" : snackbar.severity === "warning" ? "#f59e0b" : "#60a5fa",
            color: "white",
            "& .MuiAlert-icon": { color: "white" },
            "& .MuiAlert-action svg": { color: "white" },
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AllApplicationsPage;
